import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, TemplateRef } from '@angular/core';

import { Force } from '../../models';
import { ForceFolder, ForceOrganisationItem } from '../models';

export interface RenderItem {
    kind: 'root-force' | 'folder';
    force?: Force;
    folder?: ForceFolder;
    forces?: Force[];
    sortOrder: number;
    folderName?: string;
    /**
     * Pre-computed in buildRenderedItems rather than read via a template method
     * call — see the Angular template performance rules: a `isSelected(item)`
     * binding re-runs on every change-detection cycle. Always false for folders,
     * which cannot be moved under the current flat model.
     */
    selected: boolean;
}

@Component({
    standalone: false,
    selector: 'abs-force-custom-list',
    template: `
        <ng-container>
            <abs-list
                [items]="renderedItems"
                [template]="combinedTemplate"
                [customRowButtonsTemplate]="rootSwipeTemplate"
                [menuTemplate]="rootMenuTemplate"
                [itemOptionsMode]="'menu'"
                [itemOptionsVisibility]="'always'"
                [getRouterLink]="reorderMode || moveMultipleMode ? null : getItemRouterLink"
                [getQueryParams]="reorderMode || moveMultipleMode ? null : getItemQueryParams"
                [isSwipeable]="isItemSwipeable"
                [reorderable]="reorderMode && !searchTerm"
                [trackBy]="trackByRenderItem"
                (itemReorder)="onAbsListReorder($event)"
                (deleteItem)="onSwipeDelete($event)"
                (itemSelect)="onRowSelect($event.item)"
                (itemLongPress)="itemLongPress.emit()"
            ></abs-list>

            <ng-template #rootSwipeTemplate let-item="item">
                @if (item.kind === 'root-force' && item.force) {
                    <ion-item-option color="secondary" (click)="duplicateForce.emit(item.force!)">
                        <ion-icon slot="icon-only" name="copy-outline"></ion-icon>
                    </ion-item-option>
                    <ion-item-option
                        color="primary"
                        *ngIf="folders.length > 0"
                        (click)="moveToFolder.emit({ force: item.force!, folderId: null })"
                    >
                        <ion-icon slot="icon-only" name="folder-open-outline"></ion-icon>
                    </ion-item-option>
                    <ion-item-option color="danger" expandable (click)="deleteForce.emit(item.force!)">
                        <ion-icon slot="icon-only" name="trash-outline"></ion-icon>
                    </ion-item-option>
                }
                @if (item.kind === 'folder' && item.folder) {
                    <ion-item-option color="danger" expandable>
                        <ion-icon slot="icon-only" name="trash-outline"></ion-icon>
                    </ion-item-option>
                }
            </ng-template>

            <ng-template #rootMenuTemplate let-item="item">
                @if (item.kind === 'root-force' && item.force) {
                    <ion-list lines="none" style="padding:0">
                        <ion-item button (click)="duplicateForce.emit(item.force!)">
                            <ion-icon slot="start" name="copy-outline"></ion-icon>
                            {{ 'FORCES.MENU.DUPLICATE_FORCE' | translate }}
                        </ion-item>
                        <ion-item button *ngIf="folders.length > 0" (click)="moveToFolder.emit({ force: item.force!, folderId: null })">
                            <ion-icon slot="start" name="folder-open-outline"></ion-icon>
                            {{ 'FORCES.ORGANISATION.MOVE_TO_FOLDER' | translate }}
                        </ion-item>
                        <ion-item button (click)="deleteForce.emit(item.force!)">
                            <ion-icon slot="start" name="trash-outline" color="danger"></ion-icon>
                            <ion-label color="danger">{{ 'FORCES.ORGANISATION.DELETE_FORCE' | translate }}</ion-label>
                        </ion-item>
                    </ion-list>
                }
                @if (item.kind === 'folder' && item.folder) {
                    <ion-list lines="none" style="padding:0">
                        <ion-item button (click)="renameFolder.emit({ folderId: item.folder.id, name: item.folder.name })">
                            <ion-icon slot="start" name="pencil-outline"></ion-icon>
                            {{ 'FORCES.ORGANISATION.RENAME_FOLDER' | translate }}
                        </ion-item>
                        <ion-item button (click)="deleteFolder.emit(item.folder.id)">
                            <ion-icon slot="start" name="trash-outline" color="danger"></ion-icon>
                            <ion-label color="danger">{{ 'FORCES.ORGANISATION.DELETE_FOLDER' | translate }}</ion-label>
                        </ion-item>
                    </ion-list>
                }
            </ng-template>

            <ng-template #combinedTemplate let-item="item">
                @if (item.kind === 'root-force' && item.force) {
                    @if (moveMultipleMode) {
                        <ion-checkbox
                            class="force-select-checkbox"
                            slot="start"
                            [checked]="item.selected"
                            (ionChange)="onRowSelect(item)"
                            (click)="$event.stopPropagation()"
                        ></ion-checkbox>
                    }
                    <ng-container *ngTemplateOutlet="rowTemplate; context: { item: item.force }"></ng-container>
                    <ion-note *ngIf="searchTerm && item.folderName" slot="helper">
                        <ion-icon name="folder-outline"></ion-icon>
                        {{ 'FORCES.ORGANISATION.IN_FOLDER' | translate: { folderName: item.folderName } }}
                    </ion-note>
                }
                @if (item.kind === 'folder' && item.folder) {
                    <abs-force-folder-item [folder]="item.folder" [forceCount]="(item.forces || []).length"></abs-force-folder-item>
                }
            </ng-template>
        </ng-container>
    `
})
export class ForceCustomListComponent implements OnChanges {
    @Input() ownForces: Force[] = [];
    @Input() sharedForces: Force[] = [];
    @Input() orgItems: ForceOrganisationItem[] = [];
    @Input() folders: ForceFolder[] = [];
    @Input() searchTerm = '';
    @Input() getRouterLink: (force: Force) => string[] = (f) => [f.id];
    @Input() rowTemplate: TemplateRef<any>;
    @Input() reorderMode = false;
    @Input() moveMultipleMode = false;
    @Input() selectedForceIds: Set<string> = new Set<string>();

    @Output() reorderRoot = new EventEmitter<{ items: ForceOrganisationItem[]; folders: ForceFolder[] }>();
    @Output() renameFolder = new EventEmitter<{ folderId: string; name: string }>();
    @Output() deleteFolder = new EventEmitter<string>();
    @Output() moveToFolder = new EventEmitter<{ force: Force; folderId: string | null }>();
    @Output() deleteForce = new EventEmitter<Force>();
    @Output() duplicateForce = new EventEmitter<Force>();
    @Output() itemLongPress = new EventEmitter<void>();
    @Output() toggleForceSelection = new EventEmitter<string>();

    renderedItems: RenderItem[] = [];
    // Updated synchronously during reorder so buildRenderedItems has the position before store propagates
    private emptyFolderSortOrders = new Map<string, number>();

    ngOnChanges(changes: SimpleChanges): void {
        if (
            changes['ownForces'] ||
            changes['orgItems'] ||
            changes['folders'] ||
            changes['searchTerm'] ||
            changes['selectedForceIds'] ||
            changes['moveMultipleMode']
        ) {
            this.buildRenderedItems();
        }
    }

    /**
     * Row taps toggle selection instead of navigating while move-multiple is on,
     * so the whole row is the hit target rather than just the checkbox. Folders
     * are inert: there is nowhere to move a folder to under the flat model.
     */
    onRowSelect(item: RenderItem): void {
        if (!this.moveMultipleMode || item.kind !== 'root-force' || !item.force) {
            return;
        }
        this.toggleForceSelection.emit(item.force.id);
    }

    private buildRenderedItems(): void {
        const allForces = [...(this.ownForces || [])];
        const items = this.orgItems || [];
        const folders = this.folders || [];

        if (this.searchTerm) {
            this.renderedItems = this.buildSearchResults(allForces, items, folders);
            return;
        }

        const selectedIds = this.selectedForceIds ?? new Set<string>();
        const orderedItems = [...items].sort((a, b) => a.sortOrder - b.sortOrder);
        const result: RenderItem[] = [];
        const seenFolders = new Set<string>();
        const usedForceIds = new Set<string>();

        for (const item of orderedItems) {
            if (item.type !== 'own') continue;
            if (item.folderId) {
                if (!seenFolders.has(item.folderId)) {
                    seenFolders.add(item.folderId);
                    const folder = folders.find((f) => f.id === item.folderId);
                    if (folder) {
                        const folderForceIds = orderedItems
                            .filter((i) => i.folderId === folder.id && i.type === 'own')
                            .map((i) => i.forceId);
                        const folderForces = folderForceIds.map((id) => allForces.find((f) => f.id === id)).filter((f): f is Force => !!f);
                        folderForceIds.forEach((id) => usedForceIds.add(id));
                        result.push({
                            kind: 'folder',
                            folder,
                            forces: folderForces,
                            sortOrder: folder.sortOrder ?? item.sortOrder,
                            selected: false
                        });
                    }
                }
            } else {
                const force = allForces.find((f) => f.id === item.forceId);
                if (force) {
                    usedForceIds.add(force.id);
                    result.push({ kind: 'root-force', force, sortOrder: item.sortOrder, selected: selectedIds.has(force.id) });
                }
            }
        }

        // Append forces not yet in the org document
        const maxOrder = result.reduce((m, r) => Math.max(m, r.sortOrder), 0);
        allForces
            .filter((f) => !usedForceIds.has(f.id))
            .forEach((force, i) =>
                result.push({ kind: 'root-force', force, sortOrder: maxOrder + (i + 1) * 1000, selected: selectedIds.has(force.id) })
            );

        // Append empty folders: check local cache first, then persisted sortOrder, then end of list
        const afterAll = result.reduce((m, r) => Math.max(m, r.sortOrder), maxOrder);
        folders
            .filter((f) => !seenFolders.has(f.id))
            .forEach((folder, i) =>
                result.push({
                    kind: 'folder',
                    folder,
                    forces: [],
                    sortOrder: this.emptyFolderSortOrders.get(folder.id) ?? folder.sortOrder ?? afterAll + (i + 1) * 1000,
                    selected: false
                })
            );

        // Re-sort everything so persisted empty-folder sortOrders are honoured
        result.sort((a, b) => a.sortOrder - b.sortOrder);
        this.renderedItems = result;
    }

    private buildSearchResults(allForces: Force[], items: ForceOrganisationItem[], folders: ForceFolder[]): RenderItem[] {
        const term = this.searchTerm.toLowerCase();
        const selectedIds = this.selectedForceIds ?? new Set<string>();
        return allForces
            .filter((f) => f.name?.toLowerCase().includes(term))
            .map((force) => {
                const item = items.find((i) => i.forceId === force.id);
                const folder = item?.folderId ? folders.find((f) => f.id === item.folderId) : undefined;
                return {
                    kind: 'root-force' as const,
                    force,
                    sortOrder: item?.sortOrder ?? 0,
                    folderName: folder?.name,
                    selected: selectedIds.has(force.id)
                };
            });
    }

    trackByRenderItem = (_: number, item: RenderItem): string =>
        item.kind === 'folder' ? `folder-${item.folder?.id}` : `force-${item.force?.id}`;

    getItemRouterLink = (item: RenderItem): string[] | null =>
        this.moveMultipleMode
            ? null
            : item.kind === 'root-force' && item.force
              ? this.getRouterLink(item.force)
              : item.kind === 'folder'
                ? []
                : null;

    getItemQueryParams = (item: RenderItem): any => (item.kind === 'folder' && item.folder ? { folder: item.folder.id } : null);

    // Folders are always swipeable; a force only while it is unlocked, so that a locked one is not
    // animated away by a delete its owner is going to refuse.
    isItemSwipeable = (item: RenderItem): boolean => (item.kind === 'root-force' && !(item.force as any)?.locked) || item.kind === 'folder';

    trackByItem(_: number, item: RenderItem): string {
        return item.kind === 'folder' ? `folder-${item.folder!.id}` : `force-${item.force!.id}`;
    }

    onAbsListReorder(ev: { from: number; to: number }): void {
        const newRendered = [...this.renderedItems];
        const [moved] = newRendered.splice(ev.from, 1);
        newRendered.splice(ev.to, 0, moved);

        const newItems: ForceOrganisationItem[] = [];
        const updatedFolders: ForceFolder[] = [...this.folders];
        let sortOrder = 0;
        for (const item of newRendered) {
            if (item.kind === 'root-force' && item.force) {
                const existing = this.orgItems.find((i) => i.forceId === item.force!.id);
                if (existing) {
                    newItems.push({ ...existing, sortOrder });
                    sortOrder += 1000;
                }
            } else if (item.kind === 'folder' && item.folder) {
                const idx = updatedFolders.findIndex((f) => f.id === item.folder!.id);
                if ((item.forces || []).length > 0) {
                    if (idx !== -1) updatedFolders[idx] = { ...updatedFolders[idx], sortOrder };
                    for (const force of item.forces!) {
                        const existing = this.orgItems.find((i) => i.forceId === force.id);
                        if (existing) newItems.push({ ...existing, sortOrder: sortOrder++ });
                    }
                    sortOrder = Math.ceil(sortOrder / 1000) * 1000;
                } else {
                    // Empty folder: cache position locally (synchronous) and persist via folder.sortOrder
                    this.emptyFolderSortOrders.set(item.folder!.id, sortOrder);
                    if (idx !== -1) updatedFolders[idx] = { ...updatedFolders[idx], sortOrder };
                    sortOrder += 1000;
                }
            }
        }
        const seen = new Set(newItems.map((i) => i.forceId));
        for (const item of this.orgItems) {
            if (!seen.has(item.forceId)) newItems.push({ ...item, sortOrder: sortOrder++ });
        }
        this.reorderRoot.emit({ items: newItems, folders: updatedFolders });
    }

    onSwipeDelete(event: { item: any; callback: (deleted: boolean) => void }): void {
        if (event.item.kind === 'root-force' && event.item.force) {
            this.deleteForce.emit(event.item.force);
        } else if (event.item.kind === 'folder' && event.item.folder) {
            this.deleteFolder.emit(event.item.folder.id);
        } else {
            event.callback(false); // cancel animation for non-swipeable items (folders)
        }
    }

    onMoveToFolder(force: Force): void {
        this.moveToFolder.emit({ force, folderId: null });
    }
}
